// ImportManager
// Handles importing files from local filesystem and Google Drive

import { api } from '../services/api';
import { fileImportService, type ImportedFile } from '../services/file-import';
import { gdriveService, type GDriveFile } from '../services/gdrive';

export interface ImportCallbacks {
  onImportSuccess: (docId: string, title: string) => void;
  onImportError: (error: string) => void;
}

export class ImportManager {
  private callbacks: ImportCallbacks;

  constructor(callbacks: ImportCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Open file picker and import selected file
   */
  async openFilePicker(): Promise<void> {
    const file = await fileImportService.openFile();
    if (file) {
      await this.importFile(file);
    }
  }

  /**
   * Initialize drop zone for drag-and-drop import
   */
  initDropZone(): void {
    fileImportService.initDropZone(async (files) => {
      for (const file of files) {
        await this.importFile(file);
      }
    });
  }

  /**
   * Import a file into the system
   */
  async importFile(file: ImportedFile): Promise<void> {
    const response = await api.createDocument({
      title: file.name,
      content: file.content,
      tags: ['importado'],
      fileType: file.fileType
    });

    if (!response.success || !response.data) {
      this.callbacks.onImportError('Error al importar el archivo');
      return;
    }

    const doc = response.data;
    this.callbacks.onImportSuccess(doc.meta.id, doc.meta.title);
  }

  /**
   * Handle Google Drive connection and file selection
   */
  async handleGoogleDrive(): Promise<void> {
    const connected = await gdriveService.isConnected();

    if (!connected) {
      if (confirm('Conectar con Google Drive para acceder a tus archivos?')) {
        gdriveService.connect();
      }
      return;
    }

    const files = await gdriveService.listFiles();

    if (files.length === 0) {
      alert('No se encontraron archivos markdown en Google Drive');
      return;
    }

    this.showGDriveFilePicker(files);
  }

  /**
   * Show Google Drive file picker modal
   */
  private showGDriveFilePicker(files: GDriveFile[]): void {
    const overlay = document.getElementById('modal-overlay')!;
    const content = document.getElementById('modal-content')!;

    content.innerHTML = `
      <h2 style="margin-bottom: 16px;">Google Drive</h2>
      <div style="max-height: 400px; overflow-y: auto;">
        ${files.map(f => `
          <div class="doc-item" data-gdrive-id="${f.id}" style="cursor: pointer;">
            <div class="doc-item-title">${f.name}</div>
            <div class="doc-item-meta">${new Date(f.modifiedTime).toLocaleDateString()}</div>
          </div>
        `).join('')}
      </div>
      <div style="margin-top: 16px; display: flex; gap: 8px; justify-content: flex-end;">
        <button id="gdrive-disconnect" style="padding: 8px 16px; background: var(--error); border: none; border-radius: 6px; color: white; cursor: pointer;">Desconectar</button>
        <button id="gdrive-close" style="padding: 8px 16px; background: var(--bg-tertiary); border: 1px solid var(--border); border-radius: 6px; color: var(--text-primary); cursor: pointer;">Cerrar</button>
      </div>
    `;

    overlay.classList.remove('hidden');

    // File click handlers
    content.querySelectorAll('[data-gdrive-id]').forEach(el => {
      el.addEventListener('click', async () => {
        const fileId = el.getAttribute('data-gdrive-id')!;
        await this.importFromGDrive(fileId);
        overlay.classList.add('hidden');
      });
    });

    // Close button
    document.getElementById('gdrive-close')?.addEventListener('click', () => {
      overlay.classList.add('hidden');
    });

    // Disconnect button
    document.getElementById('gdrive-disconnect')?.addEventListener('click', async () => {
      if (confirm('¿Desconectar de Google Drive?')) {
        await gdriveService.disconnect();
        overlay.classList.add('hidden');
      }
    });

    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        overlay.classList.add('hidden');
      }
    });
  }

  /**
   * Import a file from Google Drive
   */
  private async importFromGDrive(fileId: string): Promise<void> {
    const file = await gdriveService.getFile(fileId);

    if (!file) {
      this.callbacks.onImportError('Error al cargar el archivo de Google Drive');
      return;
    }

    const response = await api.createDocument({
      title: file.name.replace(/\.[^.]+$/, ''),
      content: file.content,
      tags: ['gdrive']
    });

    if (!response.success || !response.data) {
      this.callbacks.onImportError('Error al importar el archivo');
      return;
    }

    const doc = response.data;
    this.callbacks.onImportSuccess(doc.meta.id, doc.meta.title);
  }
}
